import isError from 'lodash/isError';
import isObject from 'lodash/isObject';

import type { Logger } from '../types/logger';
import type { OnWritableHandler, Pipe, SubprocessSignal } from '../util/subprocess';
import type { FfprobeOutput } from '../util/ffprobeOutput';
import type { MediaType } from './types';

export type AssignedPipe = Pipe & { fileDescriptor: number };

export interface FfmpegParameters {
  args: string[],
  pipes?: AssignedPipe[],
  timeCallback?: (timeInSeconds: number) => void,
  frameNumberCallback?: (frameNumber: number) => void,
  errorCallback?: (errorMessage: string) => void,
  stdoutLogger?: Logger,
  stderrLogger?: Logger,
}

export interface FfmpegHttpError {
  url: string,
  code: number,
}

export interface FfmpegError {
  type: 'ffmpeg_error'
  lastLines: string[],
  /**
   * Best effort to try to extract the relevant error message from the
   * log lines
   */
  errorMessage?: string,
  /**
   * If the error is was caused by an HTTP error this field will be set
   */
  httpError: FfmpegHttpError | null,
  exitCode: number | null,
  exitSignal: NodeJS.Signals | null,
  failureCause: unknown | null,
  toString: () => string,
}

export function uriSupportsReconnect(uri: string): boolean {
  return uri.startsWith('http://') || uri.startsWith('https://');
}

export function uriSupportsTimeout(uri: string): boolean {
  return uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('udp://') || uri.startsWith('srt://');
}

export function isFfmpegError(error: any): error is FfmpegError {
  if (!isObject(error)) {
    return false;
  }

  if (!('type' in error) || ((error as any).type !== 'ffmpeg_error')) {
    return false;
  }

  return true;
}

function ffmpegFailureCauseToString(failureCause: unknown | null): string | null {
  if (isError(failureCause)) {
    return failureCause.message;
  } else if (typeof failureCause === 'string') {
    return failureCause;
  } else {
    return null;
  }
}

export function ffmpegErrorCause(error: FfmpegError): string {
  if (error.failureCause !== null) {
    return ffmpegFailureCauseToString(error.failureCause) ?? 'unknown cause';
  }
  if (error.errorMessage !== undefined) {
    return error.errorMessage;
  }
  if (error.exitSignal !== null) {
    return `Exited with signal: ${error.exitSignal}`;
  }
  if (error.exitCode !== null) {
    return `Exited with exit code: ${error.exitCode}`;
  }
  return 'Undetermined ffmpeg failure';
}

export function ffmpegErrorMessage(ffmpeg: FfmpegError): string {
  let suffix = (ffmpeg.lastLines.length > 0) ? `: ${ffmpeg.lastLines.join('\n')}` : '.';

  // If we managed to extract a complete error message from ffmpeg lets use that one
  if (ffmpeg.errorMessage !== undefined) {
    suffix = `: ${ffmpeg.errorMessage}`;
  }

  if (ffmpeg.failureCause !== null) {
    const message = ffmpegFailureCauseToString(ffmpeg.failureCause);
    if (message !== null) {
      suffix = `. Caused by: ${message}`;
    }
  }

  if (ffmpeg.exitCode) {
    return `Ffmpeg terminated with exit code ${ffmpeg.exitCode}${suffix}`;
  } else if (ffmpeg.exitSignal) {
    return `Ffmpeg terminated with signal ${ffmpeg.exitSignal}${suffix}`;
  } else {
    return `Ffmpeg terminated for unknown reason${suffix}`;
  }
}

export interface FfmpegProcess {
  kill: () => void,
  running: () => boolean,
  frameNumber: () => number,
  pid: () => number,
  wait: (timeoutMs?: number) => Promise<boolean>,
}

export interface Ffmpeg {
  launch: (parameter: FfmpegParameters) => FfmpegProcess;
}


/**
 * What ffprobe reads and reports beyond the streams and format it always reports
 */
export interface FfprobeQuery {
  analyzeDurationMs?: number,
  /**
   * Limits the streams to those of `mediaType`, or to the one at `index` among them (`-select_streams`)
   */
  selectStreams?: { mediaType: MediaType, index?: number },
  /**
   * Stops reading after this many frames or packets (`-read_intervals %+#N`)
   */
  readCount?: number,
  showFrames?: boolean,
  showPackets?: boolean,
}

export interface FfprobeSettings extends FfprobeQuery {
  probeSize?: number,
  /**
   * Set timeout for the protocols that supports it (e.g HTTP)
   */
  timeoutMs: number,
  /**
   * Kills ffprobe with SIGKILL when it runs longer than this, whatever the input. Unlike `timeoutMs`, which only
   * ends a stalled connection, this limits the total run time.
   */
  processTimeoutMs?: number,
  /**
   * Environment variables to set, or with null to remove, on top of the environment ffprobe inherits.
   */
  envOverride?: Record<string, string | null>,
}

export interface FfprobeError {
  type: 'ffprobe_error',
  parameters: string[],
  lastLines: string[],
  code: number,
  /**
   * The signal that ended ffprobe, for instance SIGKILL when `processTimeoutMs` ran out
   */
  signal: SubprocessSignal | null,
  stdout: string,
  /**
   * Best effort to try to extract the relevant error message from the
   * log lines
   */
  errorMessage: string | undefined,
  /**
   * If the error is was caused by an HTTP error this field will be set
   */
  httpError: FfmpegHttpError | null,
  mediaInfo: any | null,
  toString: () => string,
}

export function isFfprobeError(error: any): error is FfprobeError {
  if (!isObject(error)) {
    return false;
  }

  if (!('type' in error) || ((error as any).type !== 'ffprobe_error')) {
    return false;
  }

  return true;
}

export interface Ffprobe {
  probe: (input: string | OnWritableHandler, settings?: FfprobeSettings) => Promise<FfprobeOutput>
}

